import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { 
  Package, Plus, Minus, ArrowLeft, 
  ShoppingCart, RotateCcw, PackagePlus, Search, AlertCircle, Scan
} from 'lucide-react'
import { api } from '../../../lib/api'
import { useStore } from '../../../contexts/StoreContext'
import { useStoreValidation } from '../../../hooks/useStoreValidation'
import { useAuth } from '../../../contexts/AuthContext'
import toast from 'react-hot-toast'
import ProductImage from '../../../components/ProductImage'

interface Product {
  id: string
  name: string
  description?: string
  price: number
  image_url?: string
  stock_quantity: number
  is_available: boolean
  unit: string
  barcode?: string
  sku?: string
  category?: {
    name: string
    slug: string
  }
}

type Operation = 'sale' | 'return' | 'add' | null

const InventoryScanner = () => {
  const navigate = useNavigate()
  const params = useParams()
  const { user } = useAuth()
  const { activeStore } = useStore()
  const { isStoreApproved, requireApprovedStore } = useStoreValidation()
  
  const storeId = params.storeId || activeStore?.id || user?.storeId
  
  const [scannedProduct, setScannedProduct] = useState<Product | null>(null)
  const [manualBarcode, setManualBarcode] = useState('')
  const [operation, setOperation] = useState<Operation>(null)
  const [quantity, setQuantity] = useState(1)
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [recentScans, setRecentScans] = useState<Array<{ product: Product; operation: Operation; quantity: number; timestamp: Date }>>([])

  useEffect(() => {
    if (storeId && !isStoreApproved) {
      if (!requireApprovedStore('acceder al escáner de inventario')) {
        navigate('/store-owner/my-stores')
      }
    }
  }, [storeId, isStoreApproved, navigate])

  const handleScan = async (barcode: string) => {
    if (!storeId) {
      toast.error('No se ha seleccionado un negocio')
      return
    }

    setLoading(true)
    setScannedProduct(null)

    try {
      const response = await api.getProductByBarcode(storeId, barcode)
      
      if (response.success && response.data) {
        setScannedProduct(response.data)
        setQuantity(1)
        // Si no hay operación seleccionada, mostrar opciones
        if (!operation) {
          toast.success('Producto encontrado. Selecciona una operación.')
        }
      } else {
        toast.error('Producto no encontrado. ¿Deseas crearlo?')
        // Aquí podrías abrir un modal para crear el producto
      }
    } catch (error: any) {
      console.error('Error fetching product:', error)
      if (error.response?.status === 404) {
        toast.error('Producto no encontrado con este código de barras')
      } else {
        toast.error('Error al buscar el producto')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateStock = async () => {
    if (!scannedProduct || !operation || !storeId) return

    setUpdating(true)
    try {
      let result
      if (scannedProduct.barcode) {
        result = await api.updateProductStockByBarcode(
          storeId,
          scannedProduct.barcode,
          quantity,
          operation === 'sale' ? 'subtract' : operation === 'return' ? 'add' : 'add'
        )
      } else {
        result = await api.updateProductStock(
          scannedProduct.id,
          quantity,
          operation === 'sale' ? 'subtract' : operation === 'return' ? 'add' : 'add'
        )
      }

      if (result.success) {
        const updatedProduct = result.data
        setScannedProduct(updatedProduct)
        
        // Agregar a historial reciente
        setRecentScans(prev => [
          { product: updatedProduct, operation, quantity, timestamp: new Date() },
          ...prev.slice(0, 9) // Mantener solo los últimos 10
        ])

        const operationText = 
          operation === 'sale' ? 'Venta' :
          operation === 'return' ? 'Devolución' :
          'Agregar Inventario'

        toast.success(`${operationText} registrada. Stock actualizado.`)
        
        // Resetear después de 2 segundos
        setTimeout(() => {
          setScannedProduct(null)
          setOperation(null)
          setQuantity(1)
        }, 2000)
      } else {
        toast.error(result.message || 'Error al actualizar el stock')
      }
    } catch (error: any) {
      console.error('Error updating stock:', error)
      toast.error(error.response?.data?.message || 'Error al actualizar el stock')
    } finally {
      setUpdating(false)
    }
  }

  const getOperationColor = (op: Operation) => {
    switch (op) {
      case 'sale': return 'from-red-500 to-red-600'
      case 'return': return 'from-green-500 to-green-600'
      case 'add': return 'from-blue-500 to-blue-600'
      default: return 'from-gray-500 to-gray-600'
    }
  }

  const getOperationLabel = (op: Operation) => {
    switch (op) {
      case 'sale': return 'Venta'
      case 'return': return 'Devolución'
      case 'add': return 'Agregar Inventario'
      default: return 'Seleccionar Operación'
    }
  }

  if (!storeId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No se ha seleccionado un negocio</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50 pb-20 sm:pb-6">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b-2 border-gray-200/60">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors active:scale-95"
            >
              <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Gestión de Inventario</h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">Escanea códigos de barras para gestionar stock</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6">
        {/* Operation Selector */}
        <div className="mb-4 sm:mb-6">
          <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-lg border-2 border-gray-200/60 p-4 sm:p-6">
            <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-3 sm:mb-4">Tipo de Operación</h3>
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <button
                onClick={() => setOperation('sale')}
                className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl font-bold text-xs sm:text-sm transition-all duration-200 active:scale-95 ${
                  operation === 'sale'
                    ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1 sm:mb-2" />
                <span>Venta</span>
              </button>
              <button
                onClick={() => setOperation('return')}
                className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl font-bold text-xs sm:text-sm transition-all duration-200 active:scale-95 ${
                  operation === 'return'
                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <RotateCcw className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1 sm:mb-2" />
                <span>Devolución</span>
              </button>
              <button
                onClick={() => setOperation('add')}
                className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl font-bold text-xs sm:text-sm transition-all duration-200 active:scale-95 ${
                  operation === 'add'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <PackagePlus className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1 sm:mb-2" />
                <span>Agregar</span>
              </button>
            </div>
          </div>
        </div>

        {/* Barcode Input - Compatible con lector físico */}
        <div className="mb-4 sm:mb-6">
          <label className="block text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3">
            Código de Barras
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={manualBarcode}
              onChange={(e) => setManualBarcode(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && manualBarcode.trim()) {
                  handleScan(manualBarcode.trim())
                  setManualBarcode('')
                }
              }}
              placeholder="Escanea o ingresa el código de barras"
              autoFocus
              className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 text-base font-mono shadow-sm hover:shadow-md"
            />
            <button
              onClick={() => {
                if (!manualBarcode.trim()) {
                  toast.error('Ingresa un código de barras')
                  return
                }
                handleScan(manualBarcode.trim())
                setManualBarcode('')
              }}
              disabled={!manualBarcode.trim() || loading}
              className="px-5 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <Search className="w-5 h-5" />
              <span className="hidden sm:inline">Buscar</span>
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Usa tu lector físico de códigos de barras o ingresa el código manualmente
          </p>
        </div>

        {/* Scanned Product */}
        {loading && (
          <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-lg border-2 border-gray-200/60 p-8 sm:p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-600 border-t-transparent mx-auto"></div>
            <p className="mt-4 text-gray-600 font-medium">Buscando producto...</p>
          </div>
        )}

        {scannedProduct && !loading && (
          <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-lg border-2 border-gray-200/60 p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex items-start gap-4 sm:gap-6">
              {scannedProduct.image_url && (
                <div className="flex-shrink-0">
                  <ProductImage
                    imageKey={scannedProduct.image_url}
                    alt={scannedProduct.name}
                    className="w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 rounded-xl sm:rounded-2xl object-cover border-2 border-gray-200 shadow-md"
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-1 sm:mb-2">{scannedProduct.name}</h3>
                {scannedProduct.description && (
                  <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 line-clamp-2">{scannedProduct.description}</p>
                )}
                <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                    <span className="text-xs sm:text-sm font-medium text-gray-700">
                      Stock: <span className="font-bold text-gray-900">{scannedProduct.stock_quantity}</span> {scannedProduct.unit}
                    </span>
                  </div>
                  {scannedProduct.barcode && (
                    <div className="flex items-center gap-2">
                      <Scan className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                      <span className="text-xs sm:text-sm font-mono text-gray-600">{scannedProduct.barcode}</span>
                    </div>
                  )}
                </div>

                {/* Quantity Selector */}
                <div className="mb-4 sm:mb-5">
                  <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">Cantidad</label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center justify-center transition-colors active:scale-95"
                    >
                      <Minus className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="flex-1 px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-xl sm:rounded-2xl text-center text-base sm:text-lg font-bold text-gray-900 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
                    />
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center justify-center transition-colors active:scale-95"
                    >
                      <Plus className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
                    </button>
                  </div>
                </div>

                {/* Action Button */}
                <button
                  onClick={handleUpdateStock}
                  disabled={updating || (operation === 'sale' && quantity > scannedProduct.stock_quantity)}
                  className={`w-full px-4 sm:px-6 py-3 sm:py-3.5 bg-gradient-to-r ${getOperationColor(operation)} text-white rounded-xl sm:rounded-2xl text-sm sm:text-base font-bold hover:shadow-lg transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-md`}
                >
                  {updating ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Procesando...
                    </span>
                  ) : (
                    <>
                      {operation === 'sale' && <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 inline mr-2" />}
                      {operation === 'return' && <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5 inline mr-2" />}
                      {operation === 'add' && <PackagePlus className="w-4 h-4 sm:w-5 sm:h-5 inline mr-2" />}
                      {getOperationLabel(operation)} {quantity} {scannedProduct.unit}
                    </>
                  )}
                </button>

                {operation === 'sale' && quantity > scannedProduct.stock_quantity && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm text-red-700">
                      Stock insuficiente. Disponible: {scannedProduct.stock_quantity} {scannedProduct.unit}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Recent Scans */}
        {recentScans.length > 0 && (
          <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl shadow-lg border-2 border-gray-200/60 p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">Escaneos Recientes</h3>
            <div className="space-y-2 sm:space-y-3">
              {recentScans.map((scan, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 sm:gap-4 p-3 bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-xl sm:rounded-2xl border border-gray-200/60"
                >
                  {scan.product.image_url && (
                    <ProductImage
                      imageKey={scan.product.image_url}
                      alt={scan.product.name}
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl object-cover border border-gray-200 flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-bold text-gray-900 truncate">{scan.product.name}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600">
                      {getOperationLabel(scan.operation)} • {scan.quantity} {scan.product.unit} • {scan.product.stock_quantity} en stock
                    </p>
                  </div>
                  <div className={`px-2 sm:px-3 py-1 rounded-lg text-[10px] sm:text-xs font-bold ${
                    scan.operation === 'sale' ? 'bg-red-100 text-red-700' :
                    scan.operation === 'return' ? 'bg-green-100 text-green-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {getOperationLabel(scan.operation)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

    </div>
  )
}

export default InventoryScanner

